import { Button, Position } from '@busybusy/webapp-react-ui';
import { EquipmentModelType } from '__generated__/graphql';
import classNames from 'classnames';
import { EquipmentPicker, PanelContent } from 'components';
import EquipmentCategoryPicker from 'components/domain/equipment/EquipmentCategoryPicker/EquipmentCategoryPicker';
import EquipmentTypePicker from 'components/domain/equipment/EquipmentTypePicker/EquipmentTypePicker';
import { useQueryParams } from 'hooks';
import useEquipmentVersion2Enabled from 'hooks/feature/equipment/useEquipmentVersion2Enabled/useEquipmentVersion2Enabled';
import { isNil } from 'lodash';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import { QueryParam } from 'utils/constants/queryParam';
import { parseEquipmentModelType } from 'utils/equipmentUtils';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import './ManageEquipmentFilter.scss';

export interface IManageEquipmentFilterProps {
  className?: ClassName;
  reloadCategories?: boolean;
}

const ManageEquipmentFilter = (props: IManageEquipmentFilterProps) => {
  const { className, reloadCategories } = props;

  const { getParam, updateParams } = useQueryParams();
  const isEquipmentVersion2Enabled = useEquipmentVersion2Enabled();

  const equipmentId = getParam(QueryParam.EQUIPMENT_ID);
  const categoryId = getParam(QueryParam.EQUIPMENT_CATEGORY_ID);
  const type = isEquipmentVersion2Enabled ? parseEquipmentModelType(getParam(QueryParam.EQUIPMENT_TYPE)) : null;
  const search = getParam(QueryParam.SEARCH);
  const [localEquipmentId, setLocalEquipmentId] = useState<string | null>(equipmentId);
  const [localCategoryId, setLocalCategoryId] = useState<string | null>(categoryId);
  const [localType, setLocalType] = useState<EquipmentModelType | null>(type);
  const isManagementListsSearchEnabled = useFeatureFlags('MANAGEMENT_LISTS_SEARCH');

  function handleEquipmentChange(id: string | null) {
    setLocalEquipmentId(id);
  }

  function handleCategoryChange(id: string | null) {
    setLocalCategoryId(id);
  }

  function handleApply() {
    updateParams({
      [QueryParam.EQUIPMENT_ID]: localEquipmentId ?? null,
      [QueryParam.EQUIPMENT_CATEGORY_ID]: localCategoryId ?? null,
      [QueryParam.EQUIPMENT_TYPE]: isEquipmentVersion2Enabled ? (localType ?? null) : null,
      [QueryParam.SEARCH]: search,
    });
  }

  function handleReset() {
    setLocalEquipmentId(null);
    setLocalCategoryId(null);
    setLocalType(null);

    const params: { [key: string]: string | undefined | null } = {};
    params[QueryParam.EQUIPMENT_ID] = null;
    params[QueryParam.EQUIPMENT_CATEGORY_ID] = null;
    params[QueryParam.EQUIPMENT_TYPE] = null;
    updateParams(params);
  }

  function didChange() {
    return localEquipmentId === equipmentId && localCategoryId === categoryId && localType === type;
  }

  const classes = classNames('manage-equipment-filter', className);

  return (
    <div className={classes}>
      <PanelContent className="p-5" flex={false}>
        <h3>{t('Filter')}</h3>
        <div className="pt-5">
          {!isManagementListsSearchEnabled ? (
            <EquipmentPicker
              placeholder={t('All Equipment')}
              value={localEquipmentId}
              onSelect={handleEquipmentChange}
              position={Position.BOTTOM_END}
              className="mb-5"
            />
          ) : null}
          <EquipmentCategoryPicker
            placeholder={t('All Categories')}
            value={localCategoryId}
            onSelect={handleCategoryChange}
            position={Position.BOTTOM_END}
            className="mb-5"
            reload={reloadCategories}
          />
          {isEquipmentVersion2Enabled && (
            <EquipmentTypePicker placeholder={t('Type')} value={localType} onSelect={setLocalType} />
          )}
        </div>
        <div className="pt-5">
          <Button
            type={didChange() ? 'secondary' : 'primary'}
            disabled={didChange()}
            fullWidth={true}
            className={didChange() ? undefined : 'ac-pulse'}
            onClick={handleApply}
          >
            {t('Apply')}
          </Button>
        </div>
        {(!isNil(equipmentId) || !isNil(categoryId) || !isNil(type)) && (
          <div className="pt-2 manage-equipment-filter-reset">
            <Button type="link" onClick={handleReset}>
              {t('Reset')}
            </Button>
          </div>
        )}
      </PanelContent>
    </div>
  );
};

export default ManageEquipmentFilter;
